// Interface definitions
import { getRequest, postRequest } from '../../../services/httpOldService';
import { BatchInfo, uomDetails } from '../Common/inventory.helper';
import { calculateTotalCostOrMRP } from '../Stocks/stock.helper';
import * as Yup from 'yup';

export interface consumptionLogtabsDetails{
  label: string;
  value: list_header_type_details;
}

type list_header_type_details =  'transaction' | 'log';


export interface consumptionMaintabsDetails{
  label: string;
  value: consumption_details;
}

type consumption_details =  'consumption' | 'log_details';


export interface mappedMaterialConsumptionTableData{
  unique_row_id: string; // unique row id
  date: string;
  item_id: number;
  item_name: string;
  item_code: string;
  action: React.ReactNode;
  batch_code: string;
  batch_id: number | null;
  expiry_date: string;
  quantity: number | null;
  uom_id: number | null;
  uom: string;
  base_cost: number | null;
  total_cost: number;
  base_mrp: number | null;
  total_mrp: number;
  uom_list: uomDetails[];
  batch_list: BatchInfo[];
  conversion_factor: number;  // expected initial value: 1
  available_stock_Qty: number;
  available_stock_uom: string;
  available_stock_uom_id: number;
  total_quantity?: number;
  quantity_consumed?: number;
  quantity_remaining?: number;
  package_pharmacy_item_id?: number;
  is_perishable: boolean;
}


export const calculateRowTotalsForConsumption = (row: mappedMaterialConsumptionTableData) => {
  const quantity = row.quantity && row.quantity > 0 ? row.quantity : 0;
  const baseCost = row.base_cost || 0;
  const baseMRP = row.base_mrp || 0;

  const total_cost = calculateTotalCostOrMRP(baseCost, quantity);
  const total_mrp = calculateTotalCostOrMRP(baseMRP, quantity);


  return { total_cost: parseFloat(total_cost.toFixed(2)), total_mrp: parseFloat(total_mrp.toFixed(2)) };
};


/**
 * Calculates the total consumed quantity (in base UOM) for each PACKAGE item across all table rows.
 * Only considers package items (items with package_pharmacy_item_id).
 * Returns a map of item_id -> total converted quantity consumed.
 */
export const calculateTotalConsumedPerItem = (
  tableData: mappedMaterialConsumptionTableData[]
): Map<number, number> => {
  const itemTotals = new Map<number, number>();
  
  tableData.forEach((row) => {
    // Only include package items
    if (!row.package_pharmacy_item_id || row.quantity_remaining === undefined) {
      return;
    }
    const convertedQty = (row.quantity ?? 0) * (row.conversion_factor ?? 1);
    const currentTotal = itemTotals.get(row.item_id) || 0;
    itemTotals.set(row.item_id, currentTotal + convertedQty);
  });
  
  return itemTotals;
};


/**
 * Gets the total consumed quantity for a specific item excluding a specific row.
 * Only considers PACKAGE items (items with package_pharmacy_item_id).
 * Useful for calculating how much "budget" is left for a row.
 */
export const getTotalConsumedExcludingRow = (
  tableData: mappedMaterialConsumptionTableData[],
  itemId: number,
  excludeRowId: string
): number => {
  return tableData
    .filter((row) => 
      row.item_id === itemId && 
      row.unique_row_id !== excludeRowId &&
      // Only include package items in the calculation
      row.package_pharmacy_item_id && 
      row.quantity_remaining !== undefined
    )
    .reduce((sum, row) => sum + (row.quantity ?? 0) * (row.conversion_factor ?? 1), 0);
};


/**
 * Gets the shared limit for package items (cross-row validation).
 * For package items, this is the quantity_remaining from the package.
 * The per-batch available_stock_Qty limit is validated separately at row level.
 * For non-package items, returns null as each batch has its own available_stock_Qty limit.
 */
export const getItemLimit = (row: mappedMaterialConsumptionTableData): number | null => {
  // For package items, return package remaining quantity as the cross-item limit
  // Per-batch stock limit is checked separately in row validation
  if (row.package_pharmacy_item_id && row.quantity_remaining !== undefined) {
    return row.quantity_remaining;
  }
  // For regular items, return null - each batch has its own limit, no cross-item validation needed
  return null;
};


/**
 * Validates if the total consumed quantity for an item exceeds its limit.
 * Returns validation result with details.
 */
export interface ItemValidationResult {
  isValid: boolean;
  totalConsumed: number;
  limit: number;
  exceededBy: number;
}

export const validateItemTotalConsumption = (
  tableData: mappedMaterialConsumptionTableData[],
  itemId: number
): ItemValidationResult => {
  const itemRows = tableData.filter((row) => row.item_id === itemId);
  
  if (itemRows.length === 0) {
    return { isValid: true, totalConsumed: 0, limit: 0, exceededBy: 0 };
  }
  
  // Get the limit - only applies to package items
  const limit = getItemLimit(itemRows[0]);
  
  // If no shared limit (non-package items), validation passes at cross-item level
  if (limit === null) {
    return { isValid: true, totalConsumed: 0, limit: 0, exceededBy: 0 };
  }
  
  // Calculate total consumed for this package item
  const totalConsumed = itemRows.reduce(
    (sum, row) => sum + (row.quantity ?? 0) * (row.conversion_factor ?? 1),
    0
  );
  
  const isValid = totalConsumed <= limit;
  
  return {
    isValid,
    totalConsumed,
    limit,
    exceededBy: isValid ? 0 : totalConsumed - limit,
  };
};


/**
 * Maps PackagePharmacyItem[] to mappedMaterialConsumptionTableData[]
 * Used when adding items from package selection modal to the consumption table
 */
export const mapPackageItemsToTableData = (
  items: PackagePharmacyItem[]
): mappedMaterialConsumptionTableData[] => {
  const newRows: mappedMaterialConsumptionTableData[] = [];
  const now = Date.now();

  items.forEach((item, idx) => {
    // Skip items without batches
    if (!item.batch_object || item.batch_object.length === 0) {
      return;
    }

    const baseData: mappedMaterialConsumptionTableData = {
      date: new Date().toISOString().split('T')[0],
      item_id: item.item_id,
      item_name: item.item_name,
      item_code: item.item_code ? String(item.item_code) : '',
      action: true,
      batch_code: '',
      expiry_date: '',
      quantity: 1,
      total_quantity: item.total_quantity,
      quantity_consumed: item.quantity_consumed,
      quantity_remaining: item.quantity_remaining,
      conversion_factor: 1,
      uom_id: item.base_uom_id || item.uom_id,
      uom: item.base_uom_name || item.uom_code,
      base_cost: null,
      base_mrp: null,
      unique_row_id: `${item.item_id}-${now + idx}`,
      uom_list: item.uom_object as unknown as uomDetails[],
      batch_list: item.batch_object as unknown as BatchInfo[],
      available_stock_Qty: 0,
      available_stock_uom: '',
      available_stock_uom_id: 0,
      batch_id: null,
      total_cost: 0,
      total_mrp: 0,
      package_pharmacy_item_id: item.package_pharmacy_item_id,
      is_perishable: item.is_perishable
    };

    const totals = calculateRowTotalsForConsumption({
      ...baseData,
      total_cost: 0,
      total_mrp: 0
    });

    newRows.push({
      ...baseData,
      total_cost: totals.total_cost,
      total_mrp: totals.total_mrp,
    });
  });

  return newRows;
};


export interface PackageItem {
  id: number;
  name: string;
  code: string;
  qty: number;
  uom: string;
}

export interface PackageItemForModal {
  id: number;
  name: string;
  code: string;
  qty: number;
  uom: string;
  raw: unknown;
}

export interface materialConsumptionInitialValues{
  tableData: mappedMaterialConsumptionTableData[];
  clinic_id: number | null;
  store_id: number | null;
  isAgainstPatient : boolean;
  isScanQR: boolean;
  patient_id?: number | null;
  package_id?: number | null;
  package_name : string;
  remarks: string;
  package_pharmacy_item_id?: number;
  total_item_total_mrp: number;
  non_pkg_total_mrp: number;
  pkg_total_mrp: number;
  is_chargeable: boolean | null;
}

export const materialConsumptionInitialValue : materialConsumptionInitialValues= { 
  clinic_id: null,
  store_id: null,
  isAgainstPatient : false,
  isScanQR: false,
  patient_id: null,
  package_id: null,
  package_name : '',
  remarks: '',
  tableData: [],
  total_item_total_mrp: 0,
  non_pkg_total_mrp: 0,
  pkg_total_mrp: 0,
  is_chargeable: null
};




// Returns localized validation schema
export const getMaterialConsumptionValidationSchema = (tConsumption: (key: string) => string) =>
  Yup.object().shape({
    clinic_id: Yup.number()
      .required(tConsumption('Validation_ClinicRequired')),
    store_id: Yup.number()
      .required(tConsumption('Validation_StoreRequired')),
    isAgainstPatient : Yup.boolean(),
    isScanQR : Yup.boolean(),
    patient_id: Yup.number()
      .when('isAgainstPatient', {
        is: true,
        then: (schema) => schema.required(tConsumption('Validation_PatientRequired')),
        otherwise: (schema) => schema.nullable(),
      }),
    package_id: Yup.number()
      .nullable(),
    package_name: Yup.string()
      .nullable(),
    remarks: Yup.string()
      .max(500, tConsumption('Validation_RemarksMaxLength'))
      .nullable(),
    tableData: Yup.array()
      .of(
        Yup.object().shape({
          // batch_code: Yup.string().required(tConsumption('Validation_BatchCodeRequired')),
          // expiry_date: Yup.string().required(tConsumption('Validation_ExpiryDateRequired')),
          batch_code: Yup.string().when('is_perishable', {
            is: true,
            then: (schema) => schema.required(tConsumption('Validation_BatchCodeRequired')),
            otherwise: (schema) => schema.notRequired(),
          }),
          expiry_date: Yup.string().when('is_perishable', {
            is: true,
            then: (schema) => schema.required(tConsumption('Validation_ExpiryDateRequired')),
            otherwise: (schema) => schema.notRequired(),
          }),
          quantity: Yup.number()
            .required(tConsumption('Validation_QuantityRequired'))
            .min(1, tConsumption('Validation_QuantityMinimum'))
            .test('is-greater-or-equal', tConsumption('Validation_QuantityNotAvailable'), function (value) {
              return value * this.parent.conversion_factor <= this.parent.available_stock_Qty;
            }),
          base_cost: Yup.number()
            .required(tConsumption('Validation_BaseCostRequired'))
            .min(0, tConsumption('Validation_BaseCostMinimum'))
            .test('max-decimals', tConsumption('Validation_BaseCostMaxDecimals'), (value) => {
              if (value == null) return true; // Allow null values
              return /^\d+(\.\d{1,2})?$/.test(value.toString());
            })
            .test('is-greater-or-equal', tConsumption('Validation_BaseCostLessThanMRP'), function (value) {
              return value <= this.parent.base_mrp;
            }),
          base_mrp: Yup.number()
            .required(tConsumption('Validation_BaseMRPRequired'))
            .test('is-greater-or-equal', tConsumption('Validation_BaseMRPGreaterThanCost'), function (value) {
              return value >= this.parent.base_cost;
            })
            .min(0, tConsumption('Validation_BaseMRPMinimum'))
            .test('max-decimals', tConsumption('Validation_BaseMRPMaxDecimals'), (value) => {
              if (value == null) return true; // Allow null values
              return /^\d+(\.\d{1,2})?$/.test(value.toString());
            }),
          uom_id: Yup.number()
            .required(tConsumption('Validation_UOMRequired'))
            .min(1, tConsumption('Validation_UOMValid')),
        })
      )
      .test(
        'total-quantity-per-item',
        tConsumption('Validation_TotalQuantityExceedsLimit'),
        function (tableData) {
          if (!tableData || tableData.length === 0) return true;

          // Group rows by item_id - only for PACKAGE items
          const itemGroups = new Map<number, mappedMaterialConsumptionTableData[]>();
          tableData.forEach((row) => {
            const typedRow = row as mappedMaterialConsumptionTableData;
            // Only group package items - non-package items have per-batch limits
            if (typedRow.package_pharmacy_item_id && typedRow.quantity_remaining !== undefined) {
              const existing = itemGroups.get(typedRow.item_id) || [];
              itemGroups.set(typedRow.item_id, [...existing, typedRow]);
            }
          });

          // Validate each package item group
          const itemGroupEntries = Array.from(itemGroups.entries());
          for (let i = 0; i < itemGroupEntries.length; i++) {
            const rows = itemGroupEntries[i][1];
            if (rows.length <= 1) continue; // Single row items are validated individually

            // Calculate total consumed for this package item
            const totalConsumed = rows.reduce(
              (sum: number, row: mappedMaterialConsumptionTableData) => sum + (row.quantity ?? 0) * (row.conversion_factor ?? 1),
              0
            );

            // Get package remaining quantity limit from first row
            const firstRow = rows[0];
            const limit = firstRow.quantity_remaining ?? 0;

            if (totalConsumed > limit) {
              return false;
            }
          }

          return true;
        }
      ),
  });



export interface OpeningBalanceDetail {
  item_id: number;
  batch_id: number;
  batch_number: string | null;  // batch code 
  expiry_date: string | null;
  available_qty: number;
  available_uom_id: number;
  qty: number;
  uom_id: number;
  conversion_factor: number;  // default 1
  base_cost_price: string;
  base_mrp: string;
  total_cost_price: string;
  total_mrp: string;
  patient_id: number;
  package_id: number | null;
  package_name: string | null;
  // is_perishable: boolean;
}

interface material_consumptionsType {
      patient_id: number;
      package_id: number | null;
      package_name: string | null;
      package_pharmacy_item_id?: number;
}

export interface MaterialConsumption {
  clinic_id: number | null;
  store_id: number | null;
  is_freeze: boolean;
  is_against_patient: boolean;
  remark: string;
  is_chargeable: boolean | undefined;
  total_item_total_mrp: number;
  non_pkg_total_mrp: number;
  pkg_total_mrp: number;
  opening_balance_details: OpeningBalanceDetail[];
  material_consumptions?: material_consumptionsType[];

}


// -----------------------------
// Package pharmacy consumption
// -----------------------------

export interface PackagePharmacyUomConversion {
  id: number | null;
  item_id: number;
  item_name: string;
  uom_id: number;
  uom_code: string;
  uom_static_field: string;
  code: string;
  conversion_factor: number;
  converted_mrp: number;
  converted_cost_price: number;      
}

export interface PackagePharmacyBatch {
  batch_id: number;
  batch_number: string;
  expiry_date: string;
  base_uom_id: number;
  base_uom_code: string;
  base_cost_price: number;
  base_mrp: number;
  available_qty: number;
  available_uom_id: number;
  available_uom_code: string;
}

export interface PackagePharmacyItem {
  package_id: number;
  patient_id: number;
  item_id: number;
  item_name: string;
  uom_id: number;
  package_pharmacy_item_id?: number;
  uom_code: string;
  total_quantity: number;
  quantity_consumed: number;
  quantity_remaining: number;
  created_by: number | null;
  updated_by: number | null;
  store_id: number;
  base_uom_id: number | null;
  base_uom_code: string | null;
  uom_object: PackagePharmacyUomConversion[];
  batch_object: PackagePharmacyBatch[];
  available_qty_total: number;
  is_available: boolean;
  item_code?: string;
  base_cost: number;
  base_mrp: number;
  base_uom_name: string;
  is_perishable: boolean;
}

export interface PackagePharmacyResponse {
  package_id: string | number;
  package_name: string;
  package_net_amount: string;
  package_concession_amount: string;
  paid_amount: string;
  used_amount: string;
  balance_amount: string;
  refund_amount: string;
  hold_amount: string;
  total_service_consumable_amount: string;
  total_pharmacy_consumable_amount: string;
  is_consumable_build: boolean;
  consumable_service: boolean;
  consumable_amount: number;
  pharmacy_items: PackagePharmacyItem[];
  total_items: number;
}



export interface consumptionLogFilterValues {
    clinic_id: number | null;
    store_id: number | null;
    fromDate: string;
    toDate: string;
}



export function createMaterialConsumption(data: MaterialConsumption): Promise<MaterialConsumption> {
  const url = 'material-consumption/';  
  return postRequest(url, data, { responseType: 'json' });
}


export function getItemsPerPackage(packageId:number, store:number): Promise<PackagePharmacyResponse> {
  const url = `patient-packages/${packageId}/pharmacy-items/?store_id=${store}`;  
  return getRequest(url, { responseType: 'json' });
}



export const mapToMaterialConsumption = (data: materialConsumptionInitialValues): MaterialConsumption => {
  return {
    clinic_id: data.clinic_id,
    store_id: data.store_id,
    is_freeze: false,
    is_against_patient: data.isAgainstPatient,
    remark: data.remarks,
    is_chargeable: data.is_chargeable ?? undefined,
    total_item_total_mrp: data.total_item_total_mrp,
    non_pkg_total_mrp: data.non_pkg_total_mrp,
    pkg_total_mrp: data.pkg_total_mrp,
    opening_balance_details: data.tableData.map((item) => ({
      item_id: item.item_id,   
      batch_id: item.batch_id ?? 0, 
      // batch_number: item.batch_code,
      // expiry_date: item.expiry_date,
      batch_number: item.is_perishable ? item.batch_code : null,
      expiry_date: item.is_perishable ? item.expiry_date : null,
      qty: item.quantity || 0,
      uom_id: item.uom_id || 0,
      conversion_factor: 1, // Default conversion factor 1
      base_cost_price: item.base_cost?.toString() || '0',
      base_mrp: item.base_mrp?.toString() || '0',
      total_cost_price: item.total_cost.toString(),
      total_mrp: item.total_mrp.toString(),
      available_qty: item.available_stock_Qty,
      available_uom_id: item.available_stock_uom_id,
      // Always send patient_id if available, send null for package details if not from package
      patient_id: data.patient_id ?? 0,
      package_id: item.package_pharmacy_item_id ? (data.package_id ?? 0) : null,
      package_name: item.package_pharmacy_item_id ? data.package_name : null,
    })),
    material_consumptions: data.isAgainstPatient
      ? data.tableData
        // .filter((item) => item.package_pharmacy_item_id !== undefined && item.package_pharmacy_item_id !== null)
        .map((item) => ({
          package_pharmacy_item_id: item.package_pharmacy_item_id as number,
          patient_id: data.patient_id ?? 0,
          package_id: item.package_pharmacy_item_id ? (data.package_id ?? 0) : null,
          package_name: item.package_pharmacy_item_id ? data.package_name : null,
        }))
      : [],
    
  };
};


export const mapConsumptionLogResponse = (data: materialConsumptionInitialValues): MaterialConsumption => {
  return {
    clinic_id: data.clinic_id,
    store_id: data.store_id,
    is_freeze: false,
    is_against_patient: data.isAgainstPatient,
    remark: data.remarks,
    is_chargeable: data.is_chargeable ?? undefined,
    total_item_total_mrp: data.total_item_total_mrp,
    non_pkg_total_mrp: data.non_pkg_total_mrp,
    pkg_total_mrp: data.pkg_total_mrp,
    opening_balance_details: data.tableData.map((item) => ({
      item_id: item.item_id,   
      batch_id: item.batch_id ?? 0, 
      batch_number: item.batch_code,
      expiry_date: item.expiry_date,
      qty: item.quantity || 0,
      uom_id: item.uom_id || 0,
      conversion_factor: 1, // Default conversion factor 1
      base_cost_price: item.base_cost?.toString() || '0',
      base_mrp: item.base_mrp?.toString() || '0',
      total_cost_price: item.total_cost.toString(),
      total_mrp: item.total_mrp.toString(),
      available_qty: item.available_stock_Qty,
      available_uom_id: item.available_stock_uom_id,
      // Always send patient_id if available, send null for package details if not from package
      patient_id: data.patient_id ?? 0,
      package_id: item.package_pharmacy_item_id ? (data.package_id ?? 0) : null,
      package_name: item.package_pharmacy_item_id ? data.package_name : null,
    })),
    
  };
};





export interface GetConsumptionLogParams {
  limit?: number;
  offset?: number;
  search?: string;
  date_from?: string;
  date_to?: string;
  clinic_id?: number;
  dept_id?: number;
  is_consumption_logs?: boolean;
}

export function getConsumptionLogList(params: GetConsumptionLogParams): Promise<materialCConsumptionLogListResponse> {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });

  const url = `/consumption-logs/?${query.toString()}`;
  return getRequest(url, { responseType: 'json' });
}


export interface ItemRecord {
  date: string;
  clinic_store: string;
  quantity: string;
  base_rate: string;
  total_amount: string;
  patient_name: string;
  patient_mrn: string;
  patient_age: string;
  patient_gender: string;
  id: number;
  package_name?: string;
  package_total_qty?: number;
  package_remaining_qty?: number;
  package_uom_code?: string;
}

export interface BatchRecord {
  batch_code: string;
  id: number;
  records: ItemRecord[];
}

export interface consumptionLogResponse {
  item_code: string;
  item_name: string;
  id: number;
  qty_used: string;
  remaining_qty: string;
  total_amount: string;
  batches: BatchRecord[];
}

//----------------------------------------

export interface ConsumptionRecord {
  id: number;
  batch_code: string;
  expiry_date: string;
  clinic_store: string;
  item_code: string;
  item_name: string;
  quantity: string;
  cost_price: string;
  mrp_price: string;
  total_amount: string;
  total_cost_amount: string;
  total_mrp_amount: string;
  remark: string;
  package_name?: string;
  package_total_qty?: number;
  package_remaining_qty?: number;
  package_uom_code?: string;
}

export interface consumptionLogResponseTransactionWise {
  id: number;
  total_items: number;
  total_amount: string;
  remark: string;
  consumption_no: string;
  date: string;
  clinic_store: string;
  patient_name: string;
  patient_mrn: string;
  patient_age: string;
  patient_gender: string;
  records: ConsumptionRecord[];
}



interface materialCConsumptionLogListResponse {
  meta : {
    total_count: number;
    limit: number;
    offset: number;
  }
  objects: consumptionLogResponse[] | consumptionLogResponseTransactionWise[];
};


export const mapConsumptionLogTableData = (
  data: materialCConsumptionLogListResponse
): consumptionLogResponse[] | consumptionLogResponseTransactionWise[] => {
  return data.objects;
};

export const itemHeaderSx = {
  infoRowContainer: { mb: 0.7, alignItems: 'center' },
  infoRowLabel: { width: 200, minWidth: 90, textAlign: 'left', pr: 2, fontWeight: 500, color: 'text.secondary', fontSize: 15, letterSpacing: 0.1 },
  infoRowValue: { flex: 1, color: 'text.primary', fontWeight: 600, fontSize: 15, letterSpacing: 0.1 },
  card: { my: 2, borderRadius: 3, background: '#fafbfc', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px 0 rgba(60,72,88,0.04)' },
  cardContent: { py: 3, px: { xs: 2, md: 4 } },
  gridContainer: { mb: 1 },
  gridLeft: { pr: { md: 3 }, borderRight: { md: '1px solid #f0f0f0' } },
  gridRight: { pl: { md: 3 } },
  title: { color: 'text.primary', fontWeight: 700, mb: 2, letterSpacing: 0.2 }
};

export const mrpBoxSx = {
  container: {
    backgroundColor: '#F5F6FA',
    borderRadius: '8px',
    px: 2,
    py: 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1
  },
  containerTotal: {
    backgroundColor: '#E8F5E9',
    borderRadius: '8px',
    px: 2,
    py: 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1
  },
  label: {
    fontWeight: 500,
    color: '#5C6A7A',
    fontSize: '14px'
  },
  value: {
    fontWeight: 600,
    color: '#0F1928',
    fontSize: '14px'
  },
  labelTotal: {
    fontWeight: 500,
    color: '#2E7D32',
    fontSize: '14px'
  },
  valueTotal: {
    fontWeight: 600,
    color: '#1B5E20',
    fontSize: '14px'
  }
};

export const patientHeaderSx = {
  infoRowContainer: { mb: 0.7, alignItems: 'flex-start' },
  infoRowLabel: { width: 150, minWidth: 90, textAlign: 'left', pr: 2, fontWeight: 500, color: 'text.secondary', fontSize: 15, letterSpacing: 0.1 },
  infoRowValue: { flex: 1, color: 'text.primary', fontWeight: 600, fontSize: 15, letterSpacing: 0.1 },
  card: { my: 3, borderRadius: 3, background: '#fafbfc', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px 0 rgba(60,72,88,0.04)' },
  cardContent: { py: 3, px: { xs: 2, md: 4 } },
  gridContainer: { mb: 1 },
  gridLeft: { pr: { md: 3 }, borderRight: { md: '1px solid #f0f0f0' } },
  gridRight: { pl: { md: 3 } },
  title: { color: 'text.primary', fontWeight: 700, mb: 2, letterSpacing: 0.2 }
};